import { DOT_FILL } from './status-styles';
import type { ArgentinaMapLabels, MapJurisdiction } from './types';

/**
 * Side panel mirroring the hovered/focused cell. Informational, never a
 * dialog: role="status" + aria-live="polite", nothing to dismiss.
 */
export function MapDetailPanel({
  active,
  labels,
}: {
  active: MapJurisdiction | null;
  labels: ArgentinaMapLabels;
}) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="min-h-[180px] rounded-lg border border-line bg-paper p-5"
    >
      {active ? (
        <DetailContent jurisdiction={active} labels={labels} />
      ) : (
        <p className="text-sm leading-relaxed text-ink-muted">{labels.detailIdle}</p>
      )}
    </div>
  );
}

function DetailContent({
  jurisdiction,
  labels,
}: {
  jurisdiction: MapJurisdiction;
  labels: ArgentinaMapLabels;
}) {
  const status = labels.statuses[jurisdiction.status];
  return (
    <>
      <p className="text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-ink-muted">
        {labels.detailKicker}
      </p>
      <h3 className="mt-1 font-serif text-xl leading-tight text-ink">{jurisdiction.name}</h3>

      <div className="mt-3 inline-flex items-center gap-2 text-sm">
        <span
          aria-hidden="true"
          className={`inline-block h-2 w-2 rounded-full ${DOT_FILL[jurisdiction.status]}`}
        />
        <span className="text-ink">{status?.label ?? jurisdiction.status}</span>
      </div>

      {status?.description && (
        <p className="mt-3 text-xs leading-relaxed text-ink-muted">{status.description}</p>
      )}

      {jurisdiction.detail && jurisdiction.detail.length > 0 && (
        <dl className="mt-4 space-y-2 border-t border-line pt-4 text-xs">
          {jurisdiction.detail.map((field) => (
            <div key={field.term}>
              <dt className="uppercase tracking-wider text-ink-subtle">{field.term}</dt>
              <dd className="mt-0.5 font-medium text-ink">{field.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </>
  );
}
